import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Group } from '@mantine/core';
import { IconBell } from '@tabler/icons-react';
import { useUserStore } from '../../store/userStore';
import { useDismissHint } from '../../hooks/useOnboardingApi';
import { usePushSettings, useSubscribeDevice } from '../../hooks/usePushApi';
import { pushState, readPushEnv } from '../../utils/pushSupport';

const HINT_ID = 'push-offer';

/**
 * Предложение включить пуши. Браузерный запрос разрешения — только после клика:
 * запрос без действия пользователя браузеры запоминают как навязчивость.
 * Показывается, пока человек не решал ни «да», ни «нет»; «Не сейчас» — навсегда.
 */
export const PushOfferBanner: React.FC = () => {
  const { t } = useTranslation();
  const user = useUserStore((s) => s.user);
  const { data } = usePushSettings();
  const subscribe = useSubscribeDevice();
  const dismiss = useDismissHint();
  const [shouldShow, setShouldShow] = useState(false);

  const isDismissed = user?.onboardingState?.dismissedHints?.includes(HINT_ID) === true;

  useEffect(() => {
    if (!data?.isConfigured || isDismissed) {
      setShouldShow(false);
      return;
    }
    void readPushEnv(data.devices.map((d) => d.endpoint)).then((env) =>
      setShouldShow(pushState(env) === 'not-subscribed' && env.permission === 'default'),
    );
  }, [data, isDismissed]);

  if (!shouldShow || !data?.publicKey) return null;

  return (
    <Alert variant="light" color="teal" icon={<IconBell size={16} />} title={t('push.offerTitle')}>
      <Group gap="sm" mt="xs">
        <Button
          size="xs"
          loading={subscribe.isPending}
          onClick={() =>
            subscribe.mutate(data.publicKey!, { onSettled: () => setShouldShow(false) })
          }
        >
          {t('push.enable')}
        </Button>
        <Button size="xs" variant="subtle" onClick={() => dismiss.mutate(HINT_ID)}>
          {t('push.notNow')}
        </Button>
      </Group>
    </Alert>
  );
};
